import React, { useEffect } from "react";
import { Controller, useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  RegisterReviewSection,
  RegisterReviewSectionTitle,
} from "../../../Account/Register/Register.style";
import { COLORS } from "../../../../shared/theme/colorConfig";
import { DropdownData as US_STATES } from "../../../../components/FormElements/AddressFields/DropdownState/DropdownState.data";
import { GoogleSearchAutoComplete } from "../../../../components/FormElements/AddressFields/GoogleSearchAutoComplete/GoogleSearchAutoComplete";

// HUD Form 92006-aligned reasons for contacting the emergency contact.
const CONTACT_REASONS = [
  "emergency",
  "unableToContact",
  "terminationOfAssistance",
  "latePayment",
  "eviction",
  "leaseTermsChange",
  "houseRulesChange",
  "other",
] as const;

type HousingSituation = "renting" | "withFamilyOrFriend" | "ownHome";

// Rental history look-back required by the program. LIHTC / TDHCA typically
// expects 24 months. Configurable later per property if needed.
const RENTAL_HISTORY_THRESHOLD_MONTHS = 24;

interface CurrentAddressForm {
  housingSituation: HousingSituation;
  street: string;
  city: string;
  state: string;
  zip: string;
  moveInDate: string; // MM/YYYY
  landlord: string;
  landlordPhone: string;
  landlordEmail: string;
  reasonForLeaving: string;
}

interface PreviousAddressForm {
  housingSituation: HousingSituation;
  street: string;
  city: string;
  state: string;
  zip: string;
  moveInDate: string; // MM/YYYY
  moveOutDate: string; // MM/YYYY
  landlord: string;
  landlordPhone: string;
  landlordEmail: string;
  reasonForLeaving: string;
}

interface EmergencyContactForm {
  declined: boolean;
  name: string;
  relationship: string;
  phone: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  contactReasons: string[];
}

interface VehicleForm {
  year: string;
  make: string;
  model: string;
  color: string;
  licensePlate: string;
  licenseState: string;
}

type AnimalCategory = "pet" | "service" | "esa";

interface PetForm {
  category: AnimalCategory;
  name: string;
  type: string;
  breed: string;
  weight: string;
  color: string;
}

type UtilityIssue =
  | "outstanding_balance"
  | "credit_deposit"
  | "fraud_identity"
  | "";

export interface RentalDetailsData {
  currentAddress: CurrentAddressForm;
  // One or more prior residences, oldest-to-newest is not enforced; each covers
  // a move-in→move-out span. The applicant adds addresses until the required
  // rental-history window (RENTAL_HISTORY_THRESHOLD_MONTHS) is covered.
  previousAddresses: PreviousAddressForm[];
  canEstablishUtilities: "yes" | "no" | "";
  utilityIssue: UtilityIssue;
  hasAccessibilityNeeds: "yes" | "no" | "";
  accessibilityDetails: string;
  emergencyContact: EmergencyContactForm;
  // null until the applicant chooses. false is an explicit No.
  hasVehicles: boolean | null;
  vehicles: VehicleForm[];
  hasPets: boolean;
  pets: PetForm[];
}

// Months between MM/YYYY string and today. Returns null for invalid input.
const monthsSince = (mmYyyy: string | undefined): number | null => {
  if (!mmYyyy || !/^(0[1-9]|1[0-2])\/\d{4}$/.test(mmYyyy)) return null;
  const [mm, yyyy] = mmYyyy.split("/").map(Number);
  const now = new Date();
  return (now.getFullYear() - yyyy) * 12 + (now.getMonth() + 1 - mm);
};

// Whole months covered by a single prior tenancy (move-in → move-out, MM/YYYY).
// Returns 0 for invalid/partial input or a move-out before move-in.
export const monthsOfTenancy = (
  moveIn: string | undefined,
  moveOut: string | undefined
): number => {
  const re = /^(0[1-9]|1[0-2])\/\d{4}$/;
  if (!moveIn || !moveOut || !re.test(moveIn) || !re.test(moveOut)) return 0;
  const [inM, inY] = moveIn.split("/").map(Number);
  const [outM, outY] = moveOut.split("/").map(Number);
  const diff = (outY - inY) * 12 + (outM - inM);
  return diff > 0 ? diff : 0;
};

// Total months of rental history covered by the current address plus every
// completed prior tenancy. Used to gate the "add another address" requirement.
export const totalHistoryMonths = (
  currentMoveIn: string | undefined,
  previous: Pick<PreviousAddressForm, "moveInDate" | "moveOutDate">[]
): number => {
  const cur = monthsSince(currentMoveIn) ?? 0;
  const prev = (previous ?? []).reduce(
    (sum, a) => sum + monthsOfTenancy(a?.moveInDate, a?.moveOutDate),
    0
  );
  return cur + prev;
};

interface RentalDetailsProps {
  defaultValues?: Partial<RentalDetailsData>;
  onSubmit: (data: RentalDetailsData) => void | Promise<void>;
  onBack: () => void;
  isSaving?: boolean;
  // Unit's ResMan pet policy (MITS marketing data). When present, the pet-type
  // dropdown is populated from the allowed types instead of a static list.
  petPolicy?: any[];
}

const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
const dobRegex = /^\d{2}\/\d{2}\/\d{4}$/;
const monthYearRegex = /^(0[1-9]|1[0-2])\/\d{4}$/;
const zipRegex = /^\d{5}(-\d{4})?$/;

const formatPhone = (value: string): string => {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length < 4) return digits.length ? `(${digits}` : "";
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const formatDob = (value: string, prev: string): string => {
  if (value.length < prev.length) return value;
  const digits = value.replace(/\D/g, "").slice(0, 8);
  if (digits.length < 3) return digits;
  if (digits.length < 5) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const formatMonthYear = (value: string, prev: string): string => {
  if (value.length < prev.length) return value;
  const digits = value.replace(/\D/g, "").slice(0, 6);
  if (digits.length < 3) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

// Google Places returns the full state name (e.g. "Texas"); the state Select
// stores the 2-letter abbreviation. Map name -> abbreviation (falling back to
// an abbreviation match), else "" so the dropdown stays empty rather than wrong.
const stateNameToAbbr = (name?: string): string => {
  if (!name) return "";
  const match = US_STATES.find(
    (s) =>
      s.label.toLowerCase() === name.toLowerCase() ||
      s.abbreviation.toLowerCase() === name.toLowerCase()
  );
  return match ? match.abbreviation : "";
};

const emptyCurrentAddress: CurrentAddressForm = {
  housingSituation: "renting",
  street: "",
  city: "",
  state: "",
  zip: "",
  moveInDate: "",
  landlord: "",
  landlordPhone: "",
  landlordEmail: "",
  reasonForLeaving: "",
};

const emptyPreviousAddress: PreviousAddressForm = {
  housingSituation: "renting",
  street: "",
  city: "",
  state: "",
  zip: "",
  moveInDate: "",
  moveOutDate: "",
  landlord: "",
  landlordPhone: "",
  landlordEmail: "",
  reasonForLeaving: "",
};

const emptyEmergencyContact: EmergencyContactForm = {
  declined: false,
  name: "",
  relationship: "",
  phone: "",
  street: "",
  city: "",
  state: "",
  zip: "",
  contactReasons: [],
};

const emptyVehicle: VehicleForm = {
  year: "",
  make: "",
  model: "",
  color: "",
  licensePlate: "",
  licenseState: "",
};

const emptyPet: PetForm = {
  category: "pet",
  name: "",
  type: "",
  breed: "",
  weight: "",
  color: "",
};

export const RentalDetails: React.FC<RentalDetailsProps> = ({
  defaultValues,
  onSubmit,
  onBack,
  isSaving = false,
  petPolicy,
}) => {
  const { t } = useTranslation();

  // Generate pet type options from the unit's ResMan pet policy, mirroring the
  // conventional lease application (LeaseApplication.tsx). Falls back to a static
  // list when no unit/policy is in context (holding-mode or co-applicant flows).
  const petTypeEnum = React.useMemo(() => {
    if (!Array.isArray(petPolicy)) {
      return ["Dog", "Cat", "Bird", "Fish", "Other"];
    }
    return petPolicy
      .filter((pP: any) => pP.$Allowed === "true")
      .map((pP: any) => pP.Pets?.$PetType)
      .filter(Boolean);
  }, [petPolicy]);

  const buildAddressSchema = (isPrevious: boolean) => {
    const base: Record<string, yup.AnySchema> = {
      housingSituation: yup
        .string()
        .oneOf(["renting", "withFamilyOrFriend", "ownHome"])
        .default("renting"),
      street: yup
        .string()
        .required(
          t("affordable.application.rentalDetails.streetRequired") as string
        ),
      city: yup
        .string()
        .required(
          t("affordable.application.rentalDetails.cityRequired") as string
        ),
      state: yup
        .string()
        .required(
          t("affordable.application.rentalDetails.stateRequired") as string
        ),
      zip: yup
        .string()
        .required(
          t("affordable.application.rentalDetails.zipRequired") as string
        )
        .matches(
          zipRegex,
          t("affordable.application.rentalDetails.zipError") as string
        ),
      moveInDate: yup
        .string()
        .required(
          t("affordable.application.rentalDetails.moveInDateRequired") as string
        )
        .matches(
          monthYearRegex,
          t("affordable.application.rentalDetails.monthYearError") as string
        ),
      // Landlord fields are only validated when housingSituation === "renting".
      // Current address (isPrevious === false): BOTH phone and email are required
      // so property staff can run rental verification (Nicole, Eastside pilot).
      // Previous addresses: at least one of phone or email is enough.
      landlord: yup.string().default(""),
      landlordPhone: yup
        .string()
        .default("")
        .test(
          "phone-format",
          t("affordable.application.memberProfiles.phoneError") as string,
          (val) => !val || phoneRegex.test(val)
        )
        .test(
          "landlord-phone-required",
          t(
            "affordable.application.rentalDetails.landlordContactRequired"
          ) as string,
          function (val) {
            const housing = (this.parent as { housingSituation?: string })
              .housingSituation;
            if (housing !== "renting") return true;
            if (!isPrevious) {
              // Current address: phone is mandatory.
              return val
                ? true
                : this.createError({
                    message: t(
                      "affordable.application.rentalDetails.landlordPhoneRequired"
                    ) as string,
                  });
            }
            // Previous address: at least one of phone or email.
            const email = (this.parent as { landlordEmail?: string })
              .landlordEmail;
            return !!(val || email);
          }
        ),
      landlordEmail: yup
        .string()
        .default("")
        .test(
          "email-format",
          t(
            "affordable.application.rentalDetails.landlordEmailError"
          ) as string,
          (val) => !val || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(val)
        )
        .test(
          "landlord-email-required",
          t(
            "affordable.application.rentalDetails.landlordEmailRequired"
          ) as string,
          function (val) {
            const housing = (this.parent as { housingSituation?: string })
              .housingSituation;
            if (housing !== "renting") return true;
            // Only the current address requires email; previous addresses are
            // covered by the phone-or-email test above.
            if (!isPrevious) return !!val;
            return true;
          }
        ),
      reasonForLeaving: yup.string().default(""),
    };
    if (isPrevious) {
      base.moveOutDate = yup
        .string()
        .required(
          t(
            "affordable.application.rentalDetails.moveOutDateRequired"
          ) as string
        )
        .matches(
          monthYearRegex,
          t("affordable.application.rentalDetails.monthYearError") as string
        );
    }
    return yup.object(base);
  };

  const addressSchema = buildAddressSchema(false);
  const previousAddressSchema = buildAddressSchema(true);

  const emergencySchema = yup.object({
    // Emergency contact is mandatory in the affordable flow (no opt-out).
    declined: yup.boolean().default(false),
    name: yup
      .string()
      .required(
        t(
          "affordable.application.rentalDetails.emergencyNameRequired"
        ) as string
      ),
    relationship: yup
      .string()
      .required(
        t(
          "affordable.application.rentalDetails.emergencyRelationshipRequired"
        ) as string
      ),
    phone: yup
      .string()
      .required(
        t(
          "affordable.application.rentalDetails.emergencyPhoneRequired"
        ) as string
      )
      .matches(
        phoneRegex,
        t("affordable.application.memberProfiles.phoneError") as string
      ),
    street: yup.string().default(""),
    city: yup.string().default(""),
    state: yup.string().default(""),
    zip: yup
      .string()
      .default("")
      .test(
        "zip-format",
        t("affordable.application.rentalDetails.zipError") as string,
        (val) => !val || zipRegex.test(val)
      ),
    contactReasons: yup.array().of(yup.string().required()).default([]),
  });

  const vehicleSchema = yup.object({
    year: yup
      .string()
      .required(
        t("affordable.application.rentalDetails.vehicleYearRequired") as string
      ),
    make: yup
      .string()
      .required(
        t("affordable.application.rentalDetails.vehicleMakeRequired") as string
      ),
    model: yup
      .string()
      .required(
        t("affordable.application.rentalDetails.vehicleModelRequired") as string
      ),
    color: yup.string().default(""),
    // ResMan's lease-application API 500s on a plate containing whitespace and
    // orphans the lease, so enforce alphanumeric-only here (parity with the
    // conventional flow's plate pattern). The backend also strips whitespace as
    // a safety net.
    licensePlate: yup
      .string()
      .default("")
      .max(
        10,
        t("affordable.application.rentalDetails.vehiclePlatePattern") as string
      )
      .matches(
        /^[A-Za-z0-9]*$/,
        t("affordable.application.rentalDetails.vehiclePlatePattern") as string
      ),
    licenseState: yup.string().default(""),
  });

  const petSchema = yup.object({
    category: yup.string().oneOf(["pet", "service", "esa"]).default("pet"),
    name: yup
      .string()
      .required(
        t("affordable.application.rentalDetails.petNameRequired") as string
      ),
    type: yup
      .string()
      .required(
        t("affordable.application.rentalDetails.petTypeRequired") as string
      ),
    breed: yup.string().default(""),
    weight: yup.string().default(""),
    color: yup.string().default(""),
  });

  const schema = yup.object({
    currentAddress: addressSchema,
    // Each entered prior residence must be complete; on top of that, the
    // current address plus all prior tenancies must cover at least
    // RENTAL_HISTORY_THRESHOLD_MONTHS. If the current move-in already covers
    // the window, an empty array is valid.
    previousAddresses: yup
      .array()
      .of(previousAddressSchema)
      .default([])
      .test(
        "covers-rental-history",
        t("affordable.application.rentalDetails.rentalHistoryCoverageError", {
          months: RENTAL_HISTORY_THRESHOLD_MONTHS,
        }) as string,
        function (
          this: yup.TestContext,
          list: PreviousAddressForm[] | undefined
        ) {
          const curr = (this.parent as { currentAddress?: CurrentAddressForm })
            .currentAddress;
          // If the current move-in date is missing/invalid, let its own field
          // error surface instead of the coverage error.
          if (monthsSince(curr?.moveInDate) === null) return true;
          return (
            totalHistoryMonths(
              curr?.moveInDate,
              (list ?? []) as PreviousAddressForm[]
            ) >= RENTAL_HISTORY_THRESHOLD_MONTHS
          );
        }
      ) as any,
    canEstablishUtilities: yup
      .string()
      .oneOf(
        ["yes", "no"],
        t("affordable.application.rentalDetails.utilitiesRequired") as string
      )
      .required(
        t("affordable.application.rentalDetails.utilitiesRequired") as string
      ),
    utilityIssue: yup.string().when("canEstablishUtilities", {
      is: "no",
      then: (s) =>
        s
          .oneOf(
            ["outstanding_balance", "credit_deposit", "fraud_identity"],
            t(
              "affordable.application.rentalDetails.utilityIssueRequired"
            ) as string
          )
          .required(
            t(
              "affordable.application.rentalDetails.utilityIssueRequired"
            ) as string
          ),
      otherwise: (s) => s.notRequired(),
    }),
    hasAccessibilityNeeds: yup
      .string()
      .oneOf(
        ["yes", "no"],
        t(
          "affordable.application.rentalDetails.accessibilityRequired"
        ) as string
      )
      .required(
        t(
          "affordable.application.rentalDetails.accessibilityRequired"
        ) as string
      ),
    accessibilityDetails: yup.string().when("hasAccessibilityNeeds", {
      is: "yes",
      then: (s) =>
        s.required(
          t(
            "affordable.application.rentalDetails.accessibilityDetailsRequired"
          ) as string
        ),
      otherwise: (s) => s.notRequired(),
    }),
    emergencyContact: emergencySchema,
    hasVehicles: yup
      .boolean()
      .nullable()
      .required(
        t(
          "affordable.application.rentalDetails.vehicleAnswerRequired"
        ) as string
      ),
    vehicles: yup.array().when("hasVehicles", {
      is: true,
      then: (arr) =>
        arr
          .of(vehicleSchema)
          .min(
            1,
            t(
              "affordable.application.rentalDetails.vehicleListRequired"
            ) as string
          ),
      otherwise: (arr) => arr.of(vehicleSchema),
    }),
    hasPets: yup.boolean().default(false),
    pets: yup.array().when("hasPets", {
      is: true,
      then: (arr) => arr.of(petSchema).min(1),
      otherwise: (arr) => arr.of(petSchema),
    }),
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<RentalDetailsData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      currentAddress: {
        ...emptyCurrentAddress,
        ...(defaultValues?.currentAddress ?? {}),
      },
      previousAddresses:
        defaultValues?.previousAddresses ??
        // Migrate any legacy single previousAddress into the array.
        ((defaultValues as { previousAddress?: PreviousAddressForm })
          ?.previousAddress
          ? [
              {
                ...emptyPreviousAddress,
                ...(defaultValues as { previousAddress?: PreviousAddressForm })
                  .previousAddress,
              },
            ]
          : []),
      emergencyContact: {
        ...emptyEmergencyContact,
        ...(defaultValues?.emergencyContact ?? {}),
      },
      canEstablishUtilities: defaultValues?.canEstablishUtilities ?? "",
      utilityIssue: defaultValues?.utilityIssue ?? "",
      hasAccessibilityNeeds: defaultValues?.hasAccessibilityNeeds ?? "",
      accessibilityDetails: defaultValues?.accessibilityDetails ?? "",
      hasVehicles:
        defaultValues?.hasVehicles === true
          ? true
          : defaultValues?.hasVehicles === false
          ? false
          : null,
      vehicles: defaultValues?.vehicles ?? [],
      hasPets: defaultValues?.hasPets ?? false,
      pets: defaultValues?.pets ?? [],
    },
  });

  const hasVehicles = watch("hasVehicles");
  const hasPets = watch("hasPets");
  const hasAccessibilityNeeds = watch("hasAccessibilityNeeds");
  const canEstablishUtilities = watch("canEstablishUtilities");

  const {
    fields: vehicleFields,
    append: appendVehicle,
    remove: removeVehicle,
  } = useFieldArray({ control, name: "vehicles" });

  const {
    fields: petFields,
    append: appendPet,
    remove: removePet,
  } = useFieldArray({ control, name: "pets" });

  const {
    fields: previousAddressFields,
    append: appendPreviousAddress,
    remove: removePreviousAddress,
  } = useFieldArray({ control, name: "previousAddresses" });

  const currentHousing = watch(
    "currentAddress.housingSituation"
  ) as HousingSituation;
  const currentMoveIn = watch("currentAddress.moveInDate");
  const previousAddresses = watch("previousAddresses") || [];

  // Live rental-history coverage: current tenancy + every completed prior one.
  const monthsCovered = totalHistoryMonths(currentMoveIn, previousAddresses);
  const coverageMet = monthsCovered >= RENTAL_HISTORY_THRESHOLD_MONTHS;
  // Once we know how long they've been at the current address, keep the prior-
  // address section open until the window is covered.
  const needsMoreHistory = monthsSince(currentMoveIn) !== null && !coverageMet;

  // Seed one empty prior-address block as soon as we know more history is
  // needed and none has been added yet.
  useEffect(() => {
    if (needsMoreHistory && previousAddressFields.length === 0) {
      appendPreviousAddress(emptyPreviousAddress);
    }
  }, [needsMoreHistory, previousAddressFields.length, appendPreviousAddress]);

  const renderAddressFields = (
    prefix: string,
    housing: HousingSituation | undefined,
    moveInFieldName: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    err: any,
    showMoveOut = false,
    moveOutFieldName: string | null = null
  ) => {
    const isRenting = (housing ?? "renting") === "renting";
    return (
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <Controller
            name={`${prefix}.housingSituation` as any}
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>
                  {t("affordable.application.rentalDetails.housingSituation")}
                </InputLabel>
                <Select
                  {...field}
                  label={t(
                    "affordable.application.rentalDetails.housingSituation"
                  )}
                >
                  <MenuItem value="renting">
                    {t(
                      "affordable.application.rentalDetails.housingSituationRenting"
                    )}
                  </MenuItem>
                  <MenuItem value="withFamilyOrFriend">
                    {t(
                      "affordable.application.rentalDetails.housingSituationWithFamily"
                    )}
                  </MenuItem>
                  <MenuItem value="ownHome">
                    {t(
                      "affordable.application.rentalDetails.housingSituationOwnHome"
                    )}
                  </MenuItem>
                </Select>
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name={`${prefix}.street` as any}
            control={control}
            render={({ field }) => (
              <GoogleSearchAutoComplete
                id={`${prefix}-street`}
                label={`${t("affordable.application.rentalDetails.street")} *`}
                value={field.value || ""}
                error={err?.street?.message}
                isValid={!err?.street}
                // Free typing keeps the manual entry path working as a fallback.
                onInputChange={(val: string) => field.onChange(val ?? "")}
                // Picking a suggestion autofills the whole address; every field
                // stays editable afterward.
                onSelect={(addr: {
                  line1?: string;
                  city?: string;
                  state?: string;
                  zip?: string;
                }) => {
                  field.onChange(addr.line1 || "");
                  setValue(`${prefix}.city` as any, addr.city || "");
                  setValue(
                    `${prefix}.state` as any,
                    stateNameToAbbr(addr.state)
                  );
                  setValue(`${prefix}.zip` as any, addr.zip || "");
                  trigger([
                    `${prefix}.street`,
                    `${prefix}.city`,
                    `${prefix}.state`,
                    `${prefix}.zip`,
                  ] as any);
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name={`${prefix}.city` as any}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={`${t("affordable.application.rentalDetails.city")} *`}
                error={!!err?.city}
                helperText={err?.city?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Controller
            name={`${prefix}.state` as any}
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!err?.state}>
                <InputLabel>
                  {`${t("affordable.application.rentalDetails.state")} *`}
                </InputLabel>
                <Select
                  {...field}
                  label={`${t("affordable.application.rentalDetails.state")} *`}
                >
                  {US_STATES.map((s) => (
                    <MenuItem key={s.abbreviation} value={s.abbreviation}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
                {err?.state?.message && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, ml: 1.75 }}
                  >
                    {err.state.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Controller
            name={`${prefix}.zip` as any}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={`${t("affordable.application.rentalDetails.zip")} *`}
                error={!!err?.zip}
                helperText={err?.zip?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={showMoveOut ? 6 : 12}>
          <Controller
            name={moveInFieldName as any}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={`${t(
                  "affordable.application.rentalDetails.moveInDate"
                )} *`}
                placeholder="MM/YYYY"
                inputProps={{ inputMode: "numeric" }}
                error={!!err?.moveInDate}
                helperText={err?.moveInDate?.message}
                onChange={(e) =>
                  field.onChange(
                    formatMonthYear(e.target.value, field.value || "")
                  )
                }
              />
            )}
          />
        </Grid>
        {showMoveOut && moveOutFieldName && (
          <Grid item xs={12} sm={6}>
            <Controller
              name={moveOutFieldName as any}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={`${t(
                    "affordable.application.rentalDetails.moveOutDate"
                  )} *`}
                  placeholder="MM/YYYY"
                  inputProps={{ inputMode: "numeric" }}
                  error={!!err?.moveOutDate}
                  helperText={err?.moveOutDate?.message}
                  onChange={(e) =>
                    field.onChange(
                      formatMonthYear(e.target.value, field.value || "")
                    )
                  }
                />
              )}
            />
          </Grid>
        )}
        {isRenting && (
          <>
            <Grid item xs={12} sm={6}>
              <Controller
                name={`${prefix}.landlord` as any}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t("affordable.application.rentalDetails.landlord")}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block" }}
              >
                {t(
                  showMoveOut
                    ? "affordable.application.rentalDetails.landlordContactHelper"
                    : "affordable.application.rentalDetails.landlordContactBothHelper"
                )}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name={`${prefix}.landlordPhone` as any}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t(
                      "affordable.application.rentalDetails.landlordPhone"
                    )}
                    placeholder="(555) 555-5555"
                    inputProps={{ inputMode: "tel" }}
                    error={!!err?.landlordPhone}
                    helperText={err?.landlordPhone?.message}
                    onChange={(e) =>
                      field.onChange(formatPhone(e.target.value))
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name={`${prefix}.landlordEmail` as any}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t(
                      "affordable.application.rentalDetails.landlordEmail"
                    )}
                    type="email"
                    error={!!err?.landlordEmail}
                    helperText={
                      err?.landlordEmail?.message ||
                      t(
                        "affordable.application.rentalDetails.landlordEmailHelper"
                      )
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name={`${prefix}.reasonForLeaving` as any}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    minRows={2}
                    label={t(
                      "affordable.application.rentalDetails.reasonForLeaving"
                    )}
                  />
                )}
              />
            </Grid>
          </>
        )}
      </Grid>
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {/* Rental History */}
      <RegisterReviewSection>
        <RegisterReviewSectionTitle>
          {t("affordable.application.rentalDetails.rentalHistoryTitle")}
        </RegisterReviewSectionTitle>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("affordable.application.rentalDetails.rentalHistoryNote", {
            months: RENTAL_HISTORY_THRESHOLD_MONTHS,
          })}
        </Typography>
        <Typography variant="h3Bold" sx={{ mb: 1 }} color="primary">
          {t("affordable.application.rentalDetails.currentAddressTitle")}
        </Typography>
        {renderAddressFields(
          "currentAddress",
          currentHousing,
          "currentAddress.moveInDate",
          errors.currentAddress
        )}

        {(needsMoreHistory || previousAddressFields.length > 0) && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h3Bold" sx={{ mb: 1 }} color="primary">
              {t("affordable.application.rentalDetails.previousAddressTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("affordable.application.rentalDetails.previousAddressNote", {
                months: RENTAL_HISTORY_THRESHOLD_MONTHS,
              })}
            </Typography>

            {previousAddressFields.map((field, index) => {
              const entryHousing = (previousAddresses[index]
                ?.housingSituation ?? "renting") as HousingSituation;
              return (
                <Box key={field.id} sx={{ mt: index === 0 ? 0 : 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {t(
                        "affordable.application.rentalDetails.previousAddressHeading",
                        { number: index + 1 }
                      )}
                    </Typography>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => removePreviousAddress(index)}
                    >
                      {t("affordable.application.rentalDetails.removeAddress")}
                    </Button>
                  </Box>
                  {renderAddressFields(
                    `previousAddresses.${index}`,
                    entryHousing,
                    `previousAddresses.${index}.moveInDate`,
                    (errors.previousAddresses as any)?.[index],
                    true,
                    `previousAddresses.${index}.moveOutDate`
                  )}
                </Box>
              );
            })}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mt: 2,
              }}
            >
              <Button
                variant="outlined"
                size="small"
                onClick={() => appendPreviousAddress(emptyPreviousAddress)}
              >
                {t("affordable.application.rentalDetails.addAnotherAddress")}
              </Button>
              <Typography
                variant="caption"
                color={coverageMet ? "success.main" : "text.secondary"}
              >
                {t("affordable.application.rentalDetails.historyCovered", {
                  covered: monthsCovered,
                  months: RENTAL_HISTORY_THRESHOLD_MONTHS,
                })}
              </Typography>
            </Box>
            {typeof (errors.previousAddresses as any)?.message === "string" && (
              <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                {(errors.previousAddresses as any).message}
              </Typography>
            )}
          </Box>
        )}

        {/* Household Needs: utilities + accessibility */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h3Bold" sx={{ mb: 1 }} color="primary">
            {t("affordable.application.rentalDetails.householdNeedsTitle")}
          </Typography>

          <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
            {t("affordable.application.rentalDetails.utilitiesQuestion")}
          </Typography>
          <Controller
            name="canEstablishUtilities"
            control={control}
            render={({ field }) => (
              <FormControl
                error={!!errors.canEstablishUtilities}
                component="fieldset"
              >
                <RadioGroup {...field} row>
                  <FormControlLabel
                    value="yes"
                    control={<Radio />}
                    label={t("affordable.application.rentalDetails.yes")}
                  />
                  <FormControlLabel
                    value="no"
                    control={<Radio />}
                    label={t("affordable.application.rentalDetails.no")}
                  />
                </RadioGroup>
                {errors.canEstablishUtilities && (
                  <Typography variant="caption" color="error">
                    {errors.canEstablishUtilities.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />

          {canEstablishUtilities === "no" && (
            <Box
              sx={{ mt: 2, pl: 2, borderLeft: `3px solid ${COLORS.gray[200]}` }}
            >
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t("affordable.application.rentalDetails.utilityIssueLabel")}
              </Typography>
              <Controller
                name="utilityIssue"
                control={control}
                render={({ field }) => (
                  <FormControl
                    error={!!errors.utilityIssue}
                    component="fieldset"
                    fullWidth
                  >
                    <RadioGroup {...field}>
                      <FormControlLabel
                        value="outstanding_balance"
                        control={<Radio />}
                        label={t(
                          "affordable.application.rentalDetails.utilityIssueOutstandingBalance"
                        )}
                      />
                      <FormControlLabel
                        value="credit_deposit"
                        control={<Radio />}
                        label={t(
                          "affordable.application.rentalDetails.utilityIssueCreditDeposit"
                        )}
                      />
                      <FormControlLabel
                        value="fraud_identity"
                        control={<Radio />}
                        label={t(
                          "affordable.application.rentalDetails.utilityIssueFraudIdentity"
                        )}
                      />
                    </RadioGroup>
                    {errors.utilityIssue && (
                      <Typography variant="caption" color="error">
                        {errors.utilityIssue.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1 }}
              >
                {t(
                  "affordable.application.rentalDetails.utilityIssueFollowupNote"
                )}
              </Typography>
            </Box>
          )}

          <Typography variant="body2" sx={{ mt: 3, mb: 1 }}>
            {t("affordable.application.rentalDetails.accessibilityQuestion")}
          </Typography>
          <Controller
            name="hasAccessibilityNeeds"
            control={control}
            render={({ field }) => (
              <FormControl
                error={!!errors.hasAccessibilityNeeds}
                component="fieldset"
              >
                <RadioGroup {...field} row>
                  <FormControlLabel
                    value="yes"
                    control={<Radio />}
                    label={t("affordable.application.rentalDetails.yes")}
                  />
                  <FormControlLabel
                    value="no"
                    control={<Radio />}
                    label={t("affordable.application.rentalDetails.no")}
                  />
                </RadioGroup>
                {errors.hasAccessibilityNeeds && (
                  <Typography variant="caption" color="error">
                    {errors.hasAccessibilityNeeds.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />

          {hasAccessibilityNeeds === "yes" && (
            <Box sx={{ mt: 2 }}>
              <Controller
                name="accessibilityDetails"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    minRows={2}
                    label={`${t(
                      "affordable.application.rentalDetails.accessibilityDetails"
                    )} *`}
                    error={!!errors.accessibilityDetails}
                    helperText={
                      errors.accessibilityDetails?.message ||
                      t(
                        "affordable.application.rentalDetails.accessibilityDetailsHelper"
                      )
                    }
                  />
                )}
              />
            </Box>
          )}
        </Box>
      </RegisterReviewSection>

      {/* Emergency Contact */}
      <RegisterReviewSection>
        <RegisterReviewSectionTitle>
          {t("affordable.application.rentalDetails.emergencyContactTitle")}
        </RegisterReviewSectionTitle>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("affordable.application.rentalDetails.emergencyContactNote")}
        </Typography>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={6}>
            <Controller
              name="emergencyContact.name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={`${t(
                    "affordable.application.rentalDetails.emergencyName"
                  )} *`}
                  error={!!errors.emergencyContact?.name}
                  helperText={errors.emergencyContact?.name?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="emergencyContact.relationship"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={`${t(
                    "affordable.application.rentalDetails.emergencyRelationship"
                  )} *`}
                  error={!!errors.emergencyContact?.relationship}
                  helperText={errors.emergencyContact?.relationship?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="emergencyContact.phone"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={`${t(
                    "affordable.application.rentalDetails.emergencyPhone"
                  )} *`}
                  placeholder="(555) 555-5555"
                  inputProps={{ inputMode: "tel" }}
                  error={!!errors.emergencyContact?.phone}
                  helperText={errors.emergencyContact?.phone?.message}
                  onChange={(e) => field.onChange(formatPhone(e.target.value))}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="emergencyContact.street"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={t("affordable.application.rentalDetails.street")}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="emergencyContact.city"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={t("affordable.application.rentalDetails.city")}
                />
              )}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <Controller
              name="emergencyContact.state"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>
                    {t("affordable.application.rentalDetails.state")}
                  </InputLabel>
                  <Select
                    {...field}
                    label={t("affordable.application.rentalDetails.state")}
                  >
                    {US_STATES.map((s) => (
                      <MenuItem key={s.abbreviation} value={s.abbreviation}>
                        {s.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <Controller
              name="emergencyContact.zip"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={t("affordable.application.rentalDetails.zip")}
                  error={!!errors.emergencyContact?.zip}
                  helperText={errors.emergencyContact?.zip?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="emergencyContact.contactReasons"
              control={control}
              render={({ field }) => {
                const value = (field.value as string[]) || [];
                return (
                  <FormControl fullWidth>
                    <InputLabel>
                      {t(
                        "affordable.application.rentalDetails.emergencyReasonsLabel"
                      )}
                    </InputLabel>
                    <Select
                      multiple
                      value={value}
                      onChange={(e) =>
                        field.onChange(e.target.value as string[])
                      }
                      label={t(
                        "affordable.application.rentalDetails.emergencyReasonsLabel"
                      )}
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {(selected as string[]).map((v) => (
                            <Chip
                              key={v}
                              size="small"
                              label={t(
                                `affordable.application.rentalDetails.emergencyReasons.${v}`
                              )}
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {CONTACT_REASONS.map((reason) => (
                        <MenuItem key={reason} value={reason}>
                          <Checkbox
                            size="small"
                            checked={value.includes(reason)}
                          />
                          <ListItemText
                            primary={t(
                              `affordable.application.rentalDetails.emergencyReasons.${reason}`
                            )}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                );
              }}
            />
          </Grid>
        </Grid>
      </RegisterReviewSection>

      {/* Vehicles */}
      <RegisterReviewSection>
        <RegisterReviewSectionTitle>
          {t("affordable.application.rentalDetails.vehiclesTitle")}
        </RegisterReviewSectionTitle>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t("affordable.application.rentalDetails.hasVehicles")}
        </Typography>
        <Controller
          name="hasVehicles"
          control={control}
          render={({ field }) => (
            <FormControl error={!!errors.hasVehicles} component="fieldset">
              <RadioGroup
                row
                value={
                  field.value === true
                    ? "yes"
                    : field.value === false
                    ? "no"
                    : ""
                }
                onBlur={field.onBlur}
                onChange={(e) => {
                  const yes = e.target.value === "yes";
                  field.onChange(yes);
                  if (!yes) removeVehicle();
                  else if (vehicleFields.length === 0)
                    appendVehicle(emptyVehicle);
                }}
              >
                <FormControlLabel
                  value="yes"
                  control={<Radio />}
                  label={t("affordable.application.rentalDetails.yes")}
                />
                <FormControlLabel
                  value="no"
                  control={<Radio />}
                  label={t("affordable.application.rentalDetails.no")}
                />
              </RadioGroup>
              {errors.hasVehicles && (
                <Typography variant="caption" color="error">
                  {errors.hasVehicles.message}
                </Typography>
              )}
            </FormControl>
          )}
        />

        {hasVehicles && (
          <Box sx={{ mt: 2 }}>
            {vehicleFields.map((field, index) => {
              const err = errors.vehicles?.[index];
              return (
                <Box
                  key={field.id}
                  sx={{
                    border: `1px solid ${COLORS.gray[200]}`,
                    borderRadius: 2,
                    p: 3,
                    mb: 2,
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="h3Bold" color="primary">
                      {t("affordable.application.rentalDetails.vehicleLabel")} #
                      {index + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeVehicle(index)}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Controller
                        name={`vehicles.${index}.year`}
                        control={control}
                        render={({ field: f }) => (
                          <TextField
                            {...f}
                            fullWidth
                            label={`${t(
                              "affordable.application.rentalDetails.vehicleYear"
                            )} *`}
                            error={!!err?.year}
                            helperText={err?.year?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Controller
                        name={`vehicles.${index}.make`}
                        control={control}
                        render={({ field: f }) => (
                          <TextField
                            {...f}
                            fullWidth
                            label={`${t(
                              "affordable.application.rentalDetails.vehicleMake"
                            )} *`}
                            error={!!err?.make}
                            helperText={err?.make?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name={`vehicles.${index}.model`}
                        control={control}
                        render={({ field: f }) => (
                          <TextField
                            {...f}
                            fullWidth
                            label={`${t(
                              "affordable.application.rentalDetails.vehicleModel"
                            )} *`}
                            error={!!err?.model}
                            helperText={err?.model?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Controller
                        name={`vehicles.${index}.color`}
                        control={control}
                        render={({ field: f }) => (
                          <TextField
                            {...f}
                            fullWidth
                            label={t(
                              "affordable.application.rentalDetails.vehicleColor"
                            )}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={8} sm={5}>
                      <Controller
                        name={`vehicles.${index}.licensePlate`}
                        control={control}
                        render={({ field: f }) => (
                          <TextField
                            {...f}
                            fullWidth
                            label={t(
                              "affordable.application.rentalDetails.vehiclePlate"
                            )}
                            inputProps={{ maxLength: 10 }}
                            error={!!err?.licensePlate}
                            helperText={err?.licensePlate?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={4} sm={3}>
                      <Controller
                        name={`vehicles.${index}.licenseState`}
                        control={control}
                        render={({ field: f }) => (
                          <FormControl fullWidth>
                            <InputLabel>
                              {t(
                                "affordable.application.rentalDetails.vehicleState"
                              )}
                            </InputLabel>
                            <Select
                              {...f}
                              label={t(
                                "affordable.application.rentalDetails.vehicleState"
                              )}
                            >
                              {US_STATES.map((s) => (
                                <MenuItem
                                  key={s.abbreviation}
                                  value={s.abbreviation}
                                >
                                  {s.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      />
                    </Grid>
                  </Grid>
                </Box>
              );
            })}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => appendVehicle(emptyVehicle)}
            >
              {t("affordable.application.rentalDetails.addVehicle")}
            </Button>
            {(errors.vehicles as { message?: string } | undefined)?.message && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: "block", mt: 1 }}
              >
                {(errors.vehicles as { message?: string }).message}
              </Typography>
            )}
          </Box>
        )}
      </RegisterReviewSection>

      {/* Pets */}
      <RegisterReviewSection>
        <RegisterReviewSectionTitle>
          {t("affordable.application.rentalDetails.petsTitle")}
        </RegisterReviewSectionTitle>
        <Controller
          name="hasPets"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth>
              <InputLabel>
                {t("affordable.application.rentalDetails.hasPets")}
              </InputLabel>
              <Select
                {...field}
                value={field.value ? "yes" : "no"}
                onChange={(e) => {
                  const yes = e.target.value === "yes";
                  field.onChange(yes);
                  if (!yes) {
                    removePet();
                  } else if (petFields.length === 0) {
                    appendPet(emptyPet);
                  }
                }}
                label={t("affordable.application.rentalDetails.hasPets")}
              >
                <MenuItem value="no">
                  {t("affordable.application.rentalDetails.no")}
                </MenuItem>
                <MenuItem value="yes">
                  {t("affordable.application.rentalDetails.yes")}
                </MenuItem>
              </Select>
            </FormControl>
          )}
        />

        {hasPets && (
          <Box sx={{ mt: 2 }}>
            {petFields.map((field, index) => {
              const err = errors.pets?.[index];
              return (
                <Box
                  key={field.id}
                  sx={{
                    border: `1px solid ${COLORS.gray[200]}`,
                    borderRadius: 2,
                    p: 3,
                    mb: 2,
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="h3Bold" color="primary">
                      {t("affordable.application.rentalDetails.petLabel")} #
                      {index + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      color="error"
                      aria-label={`${t(
                        "affordable.application.rentalDetails.removePet"
                      )} ${index + 1}`}
                      onClick={() => {
                        removePet(index);
                        if (petFields.length === 1) {
                          setValue("hasPets", false);
                        }
                      }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Controller
                        name={`pets.${index}.category`}
                        control={control}
                        render={({ field: f }) => (
                          <FormControl fullWidth>
                            <InputLabel>
                              {t(
                                "affordable.application.rentalDetails.petCategory"
                              )}
                            </InputLabel>
                            <Select
                              {...f}
                              value={f.value ?? "pet"}
                              label={t(
                                "affordable.application.rentalDetails.petCategory"
                              )}
                            >
                              <MenuItem value="pet">
                                {t(
                                  "affordable.application.rentalDetails.petCategoryPet"
                                )}
                              </MenuItem>
                              <MenuItem value="service">
                                {t(
                                  "affordable.application.rentalDetails.petCategoryService"
                                )}
                              </MenuItem>
                              <MenuItem value="esa">
                                {t(
                                  "affordable.application.rentalDetails.petCategoryEsa"
                                )}
                              </MenuItem>
                            </Select>
                            {(watch(`pets.${index}.category`) === "service" ||
                              watch(`pets.${index}.category`) === "esa") && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 0.5, ml: 1.5 }}
                              >
                                {t(
                                  "affordable.application.rentalDetails.petCategoryNoFeesNote"
                                )}
                              </Typography>
                            )}
                          </FormControl>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name={`pets.${index}.name`}
                        control={control}
                        render={({ field: f }) => (
                          <TextField
                            {...f}
                            fullWidth
                            label={`${t(
                              "affordable.application.rentalDetails.petName"
                            )} *`}
                            error={!!err?.name}
                            helperText={err?.name?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name={`pets.${index}.type`}
                        control={control}
                        render={({ field: f }) => (
                          <FormControl
                            fullWidth
                            error={
                              !!(err?.type as { message?: string } | undefined)
                                ?.message
                            }
                          >
                            <InputLabel>{`${t(
                              "affordable.application.rentalDetails.petType"
                            )} *`}</InputLabel>
                            <Select
                              {...f}
                              label={`${t(
                                "affordable.application.rentalDetails.petType"
                              )} *`}
                            >
                              {petTypeEnum.map((pt: string) => (
                                <MenuItem key={pt} value={pt}>
                                  {pt}
                                </MenuItem>
                              ))}
                            </Select>
                            {(err?.type as { message?: string } | undefined)
                              ?.message && (
                              <Typography
                                variant="caption"
                                color="error"
                                sx={{ mt: 0.5, ml: 1.5 }}
                              >
                                {(err?.type as { message?: string }).message}
                              </Typography>
                            )}
                          </FormControl>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name={`pets.${index}.breed`}
                        control={control}
                        render={({ field: f }) => (
                          <TextField
                            {...f}
                            fullWidth
                            label={t(
                              "affordable.application.rentalDetails.petBreed"
                            )}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Controller
                        name={`pets.${index}.weight`}
                        control={control}
                        render={({ field: f }) => (
                          <TextField
                            {...f}
                            fullWidth
                            label={t(
                              "affordable.application.rentalDetails.petWeight"
                            )}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Controller
                        name={`pets.${index}.color`}
                        control={control}
                        render={({ field: f }) => (
                          <TextField
                            {...f}
                            fullWidth
                            label={t(
                              "affordable.application.rentalDetails.petColor"
                            )}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Box>
              );
            })}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => appendPet(emptyPet)}
            >
              {t("affordable.application.rentalDetails.addPet")}
            </Button>
          </Box>
        )}
      </RegisterReviewSection>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mt: 3,
          mb: 4,
        }}
      >
        <Button variant="outlined" onClick={onBack}>
          {t("account.leaseApplication.previous")}
        </Button>
        <Button
          variant="contained"
          type="submit"
          disabled={isSaving}
          startIcon={
            isSaving ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {t("account.leaseApplication.next")}
        </Button>
      </Box>
    </form>
  );
};
